// Summary rules editor: Check rows, their operands, and tolerances (blueprint-sets Req 6.2, 6.3).
import { useState } from 'react';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Select, { type SelectProps } from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { type CheckOp, type CheckRule, type Operand, type SummaryRules } from '@bda/shared';
import {
  type Ctx,
  DEPENDS_ON_MISSING_LABEL,
  ErrorText,
  OPERATORS,
  pathLabel,
  rowLabel,
  RuleRow,
  TOLERANCE_KINDS,
} from './summaryRulesShared';

// ---------- Checks ----------

interface OperandOption extends SelectProps.Option {
  operand: Operand;
}

export function operandOptions(rules: SummaryRules, ctx: Ctx): OperandOption[] {
  const out: OperandOption[] = [];
  rules.headerFields.forEach((h, i) => {
    const name = h.label.trim() || `Header field ${i + 1}`;
    out.push({
      value: `h|${h.id}`,
      label: name,
      description: 'Header field',
      operand: { kind: 'header', id: h.id },
    });
  });
  rules.rollups.forEach((r, i) => {
    const name = r.name.trim() || `Rollup ${i + 1}`;
    out.push({
      value: `r|${r.id}`,
      label: name,
      description: 'Rollup',
      operand: { kind: 'rollup', id: r.id },
    });
    if (r.groupBy?.kind === 'member') {
      for (const memberId of [...new Set(r.sources.map((s) => s.memberId))]) {
        const m = ctx.members.find((x) => x.blueprintId === memberId);
        out.push({
          value: `g|${r.id}|${memberId}`,
          label: `${name}: ${m?.name ?? memberId}`,
          description: 'Rollup group',
          operand: { kind: 'rollupGroup', id: r.id, group: memberId },
        });
      }
    } else if (r.groupBy?.kind === 'field') {
      out.push({
        value: `f|${r.id}`,
        label: `${name}: one group`,
        description: `Rollup group by ${pathLabel(r.groupBy.path)}`,
        operand: { kind: 'rollupGroup', id: r.id, group: '' },
      });
    }
  });
  return out;
}

/** The option an operand selects; a field-grouped rollup group matches its "one group" option whatever its value. */
function selectedOperand(o: Operand, options: OperandOption[], rules: SummaryRules): SelectProps.Option {
  const found = options.find((x) => {
    const p = x.operand;
    if (p.kind !== o.kind || p.id !== o.id) return false;
    if (p.kind !== 'rollupGroup' || o.kind !== 'rollupGroup') return true;
    return x.value!.startsWith('f|') || p.group === o.group;
  });
  if (found) return found;
  const exists =
    o.kind === 'header' ? rules.headerFields.some((h) => h.id === o.id) : rules.rollups.some((r) => r.id === o.id);
  return { value: `x|${o.kind}|${o.id}`, label: exists ? 'Group not available' : 'Removed rule' };
}

/** The tolerance box keeps what was typed ("1.", "") while the rule holds the number (NaN when blank). */
function ToleranceInput({
  value,
  row,
  readOnly,
  onChange,
}: {
  value: number;
  row: string;
  readOnly: boolean;
  onChange: (n: number) => void;
}) {
  const [text, setText] = useState<string | null>(null);
  const parsed = text === null ? NaN : text.trim() === '' ? NaN : Number(text);
  const shown =
    text !== null && (Object.is(parsed, value) || (Number.isNaN(parsed) && Number.isNaN(value)))
      ? text
      : Number.isFinite(value)
        ? String(value)
        : '';
  return (
    <Input
      type="number"
      inputMode="decimal"
      value={shown}
      readOnly={readOnly}
      ariaLabel={`${row} tolerance`}
      onChange={(e) => {
        setText(e.detail.value);
        onChange(e.detail.value.trim() === '' ? NaN : Number(e.detail.value));
      }}
    />
  );
}

export function CheckRow({
  rule,
  index,
  rules,
  ctx,
  invalidRules,
  onChange,
  onRemove,
}: {
  rule: CheckRule;
  index: number;
  rules: SummaryRules;
  ctx: Ctx;
  invalidRules: Set<string>;
  onChange: (r: CheckRule) => void;
  onRemove: () => void;
}) {
  const row = `Check ${index + 1}`;
  const key = `checks.${index}`;
  const options = operandOptions(rules, ctx);
  const dependsOnMissing =
    invalidRules.has(`${rule.left.kind === 'header' ? 'h' : 'r'}|${rule.left.id}`) ||
    invalidRules.has(`${rule.right.kind === 'header' ? 'h' : 'r'}|${rule.right.id}`);
  const operandField = (side: 'left' | 'right', label: string) => {
    const o = rule[side];
    // A group of a field-grouped rollup is named by its value on the documents, typed below the select.
    const fieldGroup = o.kind === 'rollupGroup' && options.some((x) => x.value === `f|${o.id}`);
    const error = ctx.errors[`${key}.${side}`];
    const groupError = fieldGroup && error === 'Choose a group.';
    return (
      <SpaceBetween size="xs">
        <FormField label={rowLabel(row, label)} errorText={groupError ? undefined : error}>
          <Select
            selectedOption={selectedOperand(o, options, rules)}
            options={options}
            disabled={ctx.readOnly}
            ariaLabel={`${row} ${label.toLowerCase()}`}
            onChange={(e) => {
              const next = options.find((x) => x.value === e.detail.selectedOption.value)!.operand;
              onChange({ ...rule, [side]: next });
            }}
          />
        </FormField>
        {fieldGroup && (
          <FormField
            label={rowLabel(row, `${label} group value`)}
            description="The value as it appears on the documents."
            errorText={groupError ? 'Enter the group value.' : undefined}
          >
            <Input
              value={o.group}
              readOnly={ctx.readOnly}
              ariaLabel={`${row} ${label.toLowerCase()} group value`}
              onChange={(e) => onChange({ ...rule, [side]: { ...o, group: e.detail.value } })}
            />
          </FormField>
        )}
      </SpaceBetween>
    );
  };
  return (
    <RuleRow
      title={rule.name.trim() || row}
      marker={dependsOnMissing ? DEPENDS_ON_MISSING_LABEL : undefined}
      onRemove={onRemove}
      readOnly={ctx.readOnly}
    >
      <ColumnLayout columns={3}>
        <FormField label={rowLabel(row, 'Name')} errorText={ctx.errors[`${key}.name`]}>
          <Input
            value={rule.name}
            readOnly={ctx.readOnly}
            ariaLabel={`${row} name`}
            onChange={(e) => onChange({ ...rule, name: e.detail.value })}
          />
        </FormField>
      </ColumnLayout>
      <ColumnLayout columns={3}>
        {operandField('left', 'Left value')}
        <FormField label={rowLabel(row, 'Operator')}>
          <Select
            selectedOption={OPERATORS.find((o) => o.value === rule.op)!}
            options={OPERATORS}
            disabled={ctx.readOnly}
            ariaLabel={`${row} operator`}
            onChange={(e) => onChange({ ...rule, op: e.detail.selectedOption.value as CheckOp })}
          />
        </FormField>
        {operandField('right', 'Right value')}
      </ColumnLayout>
      <ColumnLayout columns={3}>
        <FormField label={rowLabel(row, 'Tolerance type')}>
          <Select
            selectedOption={TOLERANCE_KINDS.find((t) => t.value === rule.tolerance.kind)!}
            options={TOLERANCE_KINDS}
            disabled={ctx.readOnly}
            ariaLabel={`${row} tolerance type`}
            onChange={(e) =>
              onChange({
                ...rule,
                tolerance: {
                  ...rule.tolerance,
                  kind: e.detail.selectedOption.value as 'absolute' | 'percent',
                },
              })
            }
          />
        </FormField>
        <FormField
          label={rowLabel(row, 'Tolerance')}
          constraintText={
            rule.tolerance.kind === 'percent' ? 'A percentage of the right value. 0 means exact.' : '0 means exact.'
          }
          errorText={ctx.errors[`${key}.tolerance`]}
        >
          <ToleranceInput
            value={rule.tolerance.value}
            row={row}
            readOnly={ctx.readOnly}
            onChange={(value) => onChange({ ...rule, tolerance: { ...rule.tolerance, value } })}
          />
        </FormField>
      </ColumnLayout>
      <ErrorText>{ctx.errors[`${key}.id`]}</ErrorText>
    </RuleRow>
  );
}
